import axios from 'axios';

export interface UserProfile {
  onboardingStatus: 'complete' | 'in_progress';
  coachingStyle?: 'supportive' | 'noExcuses';
  accountabilityStyle?: 'supportive' | 'noExcuses';
  identity?: string;
  focusAreas?: string[];
  onboardingCompletedAt?: string;
  name?: string;
  email?: string;
  priority?: string;
  blocker?: string;
  support?: string;
  checkinTime?: string;
}

// Helper to get auth headers including access granted status
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = token ? { 'x-auth-token': token } : {};
  
  // Add access granted header if password protection was bypassed
  const accessGranted = localStorage.getItem('thinkmate_access_granted');
  if (accessGranted === 'true') {
    headers['x-access-granted'] = 'true';
  }
  
  return headers;
};

// Get user profile from backend with localStorage fallback
export const getUserProfile = async (): Promise<UserProfile | null> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    // Fallback to localStorage
    try {
      const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return profile;
    } catch {
      return null;
    }
  }

  try {
    const response = await axios.get('/api/user/profile', {
      headers: getAuthHeaders()
    });
    return response.data as UserProfile;
  } catch {
    // Fallback to localStorage if backend fails
    try {
      const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      return profile;
    } catch {
      return null;
    }
  }
};

// Get coaching style for AI personalization
export const getCoachingStyle = (): 'supportive' | 'noExcuses' | null => {
  try {
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return profile.coachingStyle || null;
  } catch {
    return null;
  }
};

// Get user's aspirational identity
export const getUserIdentity = (): string | null => {
  try {
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return profile.identity || null;
  } catch {
    return null;
  }
};

// Get user's focus areas
export const getUserFocusAreas = (): string[] => {
  try {
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return profile.focusAreas || [];
  } catch {
    return [];
  }
};

// Check if onboarding is complete
export const isOnboardingComplete = (): boolean => {
  try {
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return profile.onboardingStatus === 'complete';
  } catch {
    return false;
  }
};

// Update user profile in both backend and localStorage
export const updateUserProfile = async (updates: Partial<UserProfile>): Promise<boolean> => {
  const token = localStorage.getItem('token');
  
  // Update localStorage immediately
  try {
    const currentProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    const updatedProfile = { ...currentProfile, ...updates };
    localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
  } catch {
    console.error('Failed to update localStorage:');
  }

  // Update backend if token exists
  if (token) {
    try {
      await axios.put('/api/user/profile', updates, {
        headers: getAuthHeaders()
      });
      return true;
    } catch (error) {
      console.error('Failed to update backend profile:', error);
      // Return false to indicate the backend update failed
      return false;
    }
  }

  return true;
};

// Get user's coaching style from profile
export const getUserCoachingStyle = (): 'supportive' | 'noExcuses' => {
  try {
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    // Map accountabilityStyle to coaching style
    return profile.accountabilityStyle === 'noExcuses' ? 'noExcuses' : 'supportive';
  } catch {
    return 'supportive'; // Default to supportive
  }
};

// Get mentor persona from onboarding answers - NEW for First Check-In Flow
export type MentorPersona = 'supportive' | 'firm';

export const getMentorPersona = (): MentorPersona => {
  try {
    // Check onboarding answers first (new format)
    const onboardingAnswers = localStorage.getItem('onboardingAnswers');
    if (onboardingAnswers) {
      const answers = JSON.parse(onboardingAnswers);
      // Map onboarding style choice to mentor persona
      if (answers.style === 'firm' || answers.style === 'Firm and focused with direct feedback') {
        return 'firm';
      } else if (answers.style === 'supportive' || answers.style === 'Encouraging with gentle guidance') {
        return 'supportive';
      }
    }
    
    // Fallback to user profile
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return profile.accountabilityStyle === 'noExcuses' ? 'firm' : 'supportive';
  } catch {
    return 'supportive'; // Default to supportive
  }
};

// Get user's name from onboarding answers
export const getUserName = (): string => {
  try {
    // Check onboarding answers first
    const onboardingAnswers = localStorage.getItem('onboardingAnswers');
    if (onboardingAnswers) {
      const answers = JSON.parse(onboardingAnswers);
      if (answers.name) {
        return answers.name;
      }
    }
    
    // Fallback to profile
    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    return profile.name || 'there';
  } catch {
    return 'there';
  }
}; 